/** "Ask a doubt" — the learner types a question in ANY language and gets it
 *  answered in that language (the doubt-clearing half of the two-agent tutor).
 *  Self-contained; drop it anywhere a learner might get stuck. An optional
 *  `context` (the German being practised) grounds the answer. */
import { useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "../api/http";
import { askDoubt } from "../api/tutor";
import { useT } from "../i18n";
import { Button, Card, Input } from "./ui";

export function AskDoubt({ context }: { context?: string }) {
  const t = useT();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const res = await askDoubt(question, context);
      if (!res.configured) {
        setError(t("doubt.off"));
      } else {
        setAnswer(res.answer);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    // data-tour so the guided tour can point at it: the client's question
    // was how a learner would ever discover this at all.
    <div data-tour="ask.doubt">
    <Card title={t("doubt.title")}>
      <p className="text-sm text-muted">{t("doubt.intro")}</p>
      <form onSubmit={ask} className="mt-3 space-y-3">
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          required
          placeholder={t("doubt.placeholder")}
          aria-label={t("doubt.title")}
        />
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <Button type="submit" loading={busy}>{t("doubt.ask")}</Button>
      </form>
      {answer && (
        <div
          role="note"
          className="mt-3 whitespace-pre-line rounded-control bg-canvas p-3 text-sm text-ink"
        >
          {answer}
        </div>
      )}
    </Card>
    </div>
  );
}
